import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, Store, Truck, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import { useCentre } from "@/context/CentreContext";

const F = "w-full bg-hp-dark border border-white/10 px-3 py-3 text-sm text-white focus:outline-none focus:border-hp-yellow";
const L = "block text-[10px] font-bold tracking-widest uppercase text-neutral-500 mb-1";

export default function CartPage() {
  const { items, setQty, remove, subtotal } = useCart();
  const { centres, current } = useCentre();
  const [fees, setFees] = useState({ shipping_fee: 0, free_shipping_from: 0 });
  const [delivery, setDelivery] = useState("pickup");
  const [centreSlug, setCentreSlug] = useState("");
  const [c, setC] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", postal_code: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [cgv, setCgv] = useState(false);
  useEffect(() => { api.get("/shop/products").then(({ data }) => setFees(data)); }, []);
  useEffect(() => { if (current && !centreSlug) setCentreSlug(current.slug); }, [current, centreSlug]);

  const shipping = delivery === "shipping" && subtotal < fees.free_shipping_from ? fees.shipping_fee : 0;
  const total = subtotal + shipping;
  const set = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, options: i.options })),
        delivery, centre_slug: delivery === "pickup" ? centreSlug : null, customer: c, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="cart-empty">
        <Seo title="Panier | Boutique Horsepower" />
        <h1 className="font-display font-black uppercase tracking-tight text-4xl">Votre panier est vide</h1>
        <Link to="/boutique" className="hp-cta inline-flex items-center gap-2 px-8 py-4 mt-8 text-sm" data-testid="cart-go-shop">Découvrir la boutique <ArrowRight size={16} /></Link>
      </div>
    );
  }

  return (
    <div className="pt-28 lg:pt-36" data-testid="cart-page">
      <Seo title="Panier | Boutique Horsepower" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">Votre panier</h1>
        <form onSubmit={pay} className="grid lg:grid-cols-[1fr_400px] gap-10 mt-10">
          <div className="space-y-8">
            <div className="space-y-3" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.key} className="hp-card p-4 flex gap-4 items-center" data-testid={`cart-item-${i.slug}`}>
                  <div className="w-20 h-20 bg-white shrink-0"><img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold uppercase tracking-tight truncate">{i.name}</p>
                    <p className="text-xs text-neutral-400">{Object.entries(i.options).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}</p>
                    <p className="text-sm mt-1">{eur(i.price)}</p>
                  </div>
                  <div className="inline-flex items-center border border-white/15">
                    <button type="button" onClick={() => setQty(i.key, i.qty - 1)} className="px-2.5 py-2 hover:text-hp-yellow"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                    <button type="button" onClick={() => setQty(i.key, i.qty + 1)} className="px-2.5 py-2 hover:text-hp-yellow"><Plus size={14} /></button>
                  </div>
                  <p className="font-display w-24 text-right">{eur(i.price * i.qty)}</p>
                  <button type="button" onClick={() => remove(i.key)} className="text-neutral-500 hover:text-red-400 p-2" data-testid={`cart-remove-${i.slug}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <div>
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl mb-4">Mode de réception</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[{ id: "pickup", icon: Store, t: "Retrait en centre", d: "Gratuit · dans votre centre Horsepower" }, { id: "shipping", icon: Truck, t: "Livraison à domicile", d: `${eur(fees.shipping_fee)} · offerte dès ${eur(fees.free_shipping_from)}` }].map((m) => (
                  <button type="button" key={m.id} onClick={() => setDelivery(m.id)} data-testid={`delivery-${m.id}`}
                    className={`hp-card p-4 text-left flex items-start gap-3 border transition-colors ${delivery === m.id ? "border-hp-yellow" : "border-transparent hover:border-white/20"}`}>
                    <m.icon className={delivery === m.id ? "text-hp-yellow" : "text-neutral-400"} size={22} />
                    <div><p className="font-bold">{m.t}</p><p className="text-xs text-neutral-400 mt-0.5">{m.d}</p></div>
                  </button>
                ))}
              </div>
              {delivery === "pickup" && (
                <div className="mt-4">
                  <label className={L}>Centre de retrait</label>
                  <select className={F} value={centreSlug} onChange={(e) => setCentreSlug(e.target.value)} required data-testid="pickup-centre">
                    <option value="">Choisir un centre…</option>
                    {centres.map((ce) => <option key={ce.slug} value={ce.slug}>{ce.name} — {ce.postal_city}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl mb-4">Vos coordonnées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={L}>Prénom</label><input className={F} value={c.first_name} onChange={set("first_name")} required data-testid="cust-first-name" /></div>
                <div><label className={L}>Nom</label><input className={F} value={c.last_name} onChange={set("last_name")} required data-testid="cust-last-name" /></div>
                <div><label className={L}>Email</label><input type="email" className={F} value={c.email} onChange={set("email")} required data-testid="cust-email" /></div>
                <div><label className={L}>Téléphone</label><input type="tel" className={F} value={c.phone} onChange={set("phone")} required data-testid="cust-phone" /></div>
                {delivery === "shipping" && (<>
                  <div className="sm:col-span-2"><label className={L}>Adresse</label><input className={F} value={c.address} onChange={set("address")} required data-testid="cust-address" /></div>
                  <div><label className={L}>Code postal</label><input className={F} value={c.postal_code} onChange={set("postal_code")} required data-testid="cust-postal" /></div>
                  <div><label className={L}>Ville</label><input className={F} value={c.city} onChange={set("city")} required data-testid="cust-city" /></div>
                </>)}
              </div>
            </div>
          </div>

          <aside className="hp-card p-6 h-fit lg:sticky lg:top-32" data-testid="cart-summary">
            <h2 className="font-display font-bold uppercase tracking-tight text-xl">Récapitulatif</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-neutral-300"><span>Sous-total</span><span data-testid="cart-subtotal">{eur(subtotal)}</span></div>
              <div className="flex justify-between text-neutral-300"><span>Livraison</span><span data-testid="cart-shipping">{delivery === "pickup" ? "Retrait gratuit" : shipping ? eur(shipping) : "Offerte"}</span></div>
              <div className="flex justify-between font-display text-2xl pt-3 border-t border-white/10"><span>Total</span><span data-testid="cart-total">{eur(total)}</span></div>
            </div>
            <label className="flex items-start gap-2 mt-5 text-xs text-neutral-400 cursor-pointer">
              <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} required className="mt-0.5 accent-hp-yellow" data-testid="cgv-checkbox" />
              <span>J'accepte les <Link to="/cgv" className="text-hp-yellow underline underline-offset-2" target="_blank">conditions générales de vente</Link> et la <Link to="/confidentialite" className="text-hp-yellow underline underline-offset-2" target="_blank">politique de confidentialité</Link>.</span>
            </label>
            <button type="submit" disabled={loading} className="hp-cta w-full mt-6 py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-btn">
              <Lock size={16} /> {loading ? "Redirection…" : "Payer en toute sécurité"}
            </button>
            <p className="text-[11px] text-neutral-500 mt-3 text-center">Paiement sécurisé par Stripe · CB, Apple Pay, Google Pay</p>
          </aside>
        </form>
      </section>
    </div>
  );
}
